import { Image } from '@chakra-ui/image';
import {
    chakra,
    Checkbox,
    Code,
    Divider,
    ListItem,
    OrderedList,
    Table,
    Tbody,
    Td,
    Th,
    Thead,
    Tr,
    UnorderedList,
} from '@chakra-ui/react';
import ReactMarkdown from 'react-markdown';
import { SpecialComponents } from 'react-markdown/lib/ast-to-react';
import { NormalComponents } from 'react-markdown/lib/complex-types';
import {
    BigHeader,
    Header,
    LinkText,
    Paragraph,
    Subheader,
    Text,
    TextProps,
} from '../common/Typography';

type ReactMarkdownComponentConfig = Partial<
    Omit<NormalComponents, keyof SpecialComponents> & SpecialComponents
>;

type ConfigProps = {
    textProps?: TextProps;
};

const makeComponentConfig = ({
    textProps,
}: ConfigProps): ReactMarkdownComponentConfig => ({
    p: ({ children }) => (
        <Paragraph mb={2} {...textProps}>
            {children}
        </Paragraph>
    ),
    em: (props) => {
        const { children } = props;
        return (
            <Text as="em" {...textProps}>
                {children}
            </Text>
        );
    },
    blockquote: (props) => {
        const { children } = props;
        return (
            <Code as="blockquote" p={2} {...textProps}>
                {children}
            </Code>
        );
    },
    code: (props) => {
        const { inline, children, className } = props;

        if (inline) {
            return (
                <Code p={2} {...textProps}>
                    {children}
                </Code>
            );
        }

        return (
            <Code
                {...textProps}
                className={className}
                whiteSpace="break-spaces"
                display="block"
                w="full"
                p={2}
            >
                {children}
            </Code>
        );
    },
    del: (props) => {
        const { children } = props;
        return (
            <Text as="del" {...textProps}>
                {children}
            </Text>
        );
    },
    hr: () => {
        return <Divider />;
    },
    a: (props: any) => (
        <LinkText
            {...textProps}
            {...props}
            target="_blank"
            withUnderline
            color="inherit"
        />
    ),
    img: Image,
    ul: (props) => {
        const { ordered, children, depth } = props;
        let Element = UnorderedList;
        let styleType = 'disc';
        if (ordered) {
            Element = OrderedList;
            styleType = 'decimal';
        }
        if (depth === 1) styleType = 'circle';
        return (
            <Element
                spacing={1}
                as={ordered ? 'ol' : 'ul'}
                styleType={styleType}
                pl={4}
                mb={4}
                fontSize="md"
            >
                {children}
            </Element>
        );
    },
    ol: (props) => {
        const { ordered, children, depth } = props;
        let Element = UnorderedList;
        let styleType = 'disc';
        if (ordered) {
            Element = OrderedList;
            styleType = 'decimal';
        }
        if (depth === 1) styleType = 'circle';
        return (
            <Element
                spacing={2}
                as={ordered ? 'ol' : 'ul'}
                styleType={styleType}
                pl={4}
                mb={4}
                fontSize="md"
            >
                {children}
            </Element>
        );
    },
    li: (props) => {
        const { children, checked } = props;
        let checkbox = null;
        if (checked !== null && checked !== undefined) {
            checkbox = (
                <Checkbox isChecked={checked} isReadOnly>
                    {children}
                </Checkbox>
            );
        }
        return (
            <ListItem listStyleType={checked !== null ? 'none' : 'inherit'}>
                <Text {...textProps}>{checkbox || children}</Text>
            </ListItem>
        );
    },
    h1: ({ children }) => <BigHeader mb={4}>{children}</BigHeader>,
    h2: ({ children }) => <Header mb={4}>{children}</Header>,
    h3: ({ children }) => <Subheader mb={3}>{children}</Subheader>,
    pre: chakra.pre,
    table: Table,
    thead: Thead,
    tbody: Tbody,
    tr: (props) => <Tr>{props.children}</Tr>,
    td: (props) => <Td>{props.children}</Td>,
    th: (props) => <Th>{props.children}</Th>,
});

const defaultConfig = Object.freeze(makeComponentConfig({}));

export interface MarkdownProps {
    content: string; // A markdown string
    configProps?: ConfigProps;
}

const Markdown = ({ content, configProps }: MarkdownProps) => {
    const config = configProps
        ? makeComponentConfig(configProps)
        : defaultConfig;
    return <ReactMarkdown components={config}>{content}</ReactMarkdown>;
};

export default Markdown;
